import { CloseOutlined, RightOutlined } from "@ant-design/icons";
import { Alert, Button } from "antd";
import { css } from "@linaria/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { UnreachableCaseError } from "ts-essentials";
import { TeamRole } from "../gql/generated/graphqlSchema.tsx";
import { type AccessState, useTeamAccessState } from "../hooks/useTeamAccessState.ts";
import { routes } from "../routes.ts";
import { formatDateTime } from "../utils/datetime.ts";

const wrapStyle = css`
  margin: var(--ant-padding) var(--ant-padding) 0;
`;

type Props = {
  readonly role: TeamRole;
};

type BannerView = {
  readonly severity: "info" | "warning" | "error";
  readonly title: string;
  readonly description: string;
  /** Trial-end reminders are dismissible per session; lockout/past-due/cancellation aren't. */
  readonly closable: boolean;
};

export const FreeAccessBanner = ({ role }: Props) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { state } = useTeamAccessState();
  const [dismissed, setDismissed] = useState(false);
  const isAdmin = role === TeamRole.Admin;

  const view = buildView(state, isAdmin, t, i18n.language);
  if (view == null || dismissed) return null;

  // Admin gets a navigation button to the Subscription tab — actual checkout / portal
  // flows live there. Members see no CTA; the message itself tells them to ask their admin.
  const action = isAdmin ? (
    <Button
      icon={<RightOutlined />}
      iconPlacement="end"
      onClick={() => navigate({ pathname: routes.myAccount.to(), hash: "billing" })}
      type="primary"
    >
      {t("billing.openSubscription")}
    </Button>
  ) : null;

  return (
    <div className={wrapStyle}>
      <Alert
        action={action}
        closable={view.closable ? { onClose: () => setDismissed(true), closeIcon: <CloseOutlined /> } : false}
        description={view.description}
        showIcon
        title={view.title}
        type={view.severity}
      />
    </div>
  );
};

const buildView = (
  state: AccessState,
  isAdmin: boolean,
  t: ReturnType<typeof useTranslation>["t"],
  locale: string,
): BannerView | null => {
  switch (state.kind) {
    case "none":
      return null;
    case "trialEndingSubtle":
      return {
        severity: "info",
        closable: true,
        title: t("billing.trialEndingTitle"),
        description: t("billing.trialDaysLeft", {
          count: state.daysLeft,
          date: formatDateTime(state.endsAt, locale),
        }),
      };
    case "trialEndingProminent":
      return {
        severity: "warning",
        closable: true,
        title: t("billing.trialEndingSoonTitle"),
        description: t("billing.trialDaysLeft", {
          count: state.daysLeft,
          date: formatDateTime(state.endsAt, locale),
        }),
      };
    case "locked":
      return {
        severity: "error",
        closable: false,
        title: t("billing.lockedTitle"),
        description: isAdmin ? t("billing.lockedAdmin") : t("billing.lockedMember"),
      };
    case "pastDue":
      return {
        severity: "warning",
        closable: false,
        title: t("billing.pastDueTitle"),
        description: isAdmin ? t("billing.pastDueAdmin") : t("billing.pastDueMember"),
      };
    case "cancelScheduled":
      return {
        severity: "info",
        closable: false,
        title: t("billing.cancelScheduledTitle"),
        description: state.endsAt
          ? t("billing.cancelScheduledBody", { date: formatDateTime(state.endsAt, locale) })
          : t("billing.cancelScheduledBare"),
      };
    default:
      throw new UnreachableCaseError(state);
  }
};
